/**
 * Zone Admin self-service dashboard (role ``ZONE_ADMIN``).
 *
 * Single-page experience modeled after the Rider PWA — KPI tiles, ledger
 * (commission earnings, delivery-fee redirects, payouts received,
 * adjustments) + a list of zones they manage and their drivers + recent
 * attributed orders.
 */
import { useEffect, useState } from "react";
import { Loader2, Coins, Truck, Wallet, MapPin, Users, ListOrdered, LogOut, RefreshCcw, Info, Store } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { api, inr } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import NotificationGate from "../../components/NotificationGate";
import OrderAlertFeed from "../../components/OrderAlertFeed";
import { toast } from "sonner";

const FILTERS = [
    ["", "all", "All"],
    ["EARNING_COMMISSION", "comm", "Commission"],
    ["EARNING_DELIVERY", "deliv", "Delivery"],
    ["PAYOUT", "payout", "Payouts"],
];

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function ZoneAdminDashboard() {
    const [me, setMe] = useState(null);
    const [tab, setTab] = useState("ledger");  // ledger | drivers | orders
    const [filter, setFilter] = useState("");
    const [ledger, setLedger] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [mRes, lRes, dRes, oRes] = await Promise.allSettled([
                api.get("/zone-admin/me"),
                api.get(`/zone-admin/earnings/ledger${filter ? `?kind=${filter}` : ""}`),
                api.get("/zone-admin/riders"),
                api.get("/zone-admin/orders"),
            ]);
            if (mRes.status === "fulfilled") setMe(mRes.value.data);
            if (lRes.status === "fulfilled") setLedger(lRes.value.data || []);
            if (dRes.status === "fulfilled") setDrivers(dRes.value.data || []);
            if (oRes.status === "fulfilled") setOrders(oRes.value.data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
        finally { setLoading(false); }
    };
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filter]);

    // The shared logout, like every other console: it ends the server session (the httpOnly cookie, which JS
    // can't clear) and unregisters this device's notifications. Clearing only localStorage left the zone admin
    // signed in by cookie and still receiving their notifications after "logging out".
    const { logout: authLogout } = useAuth();
    const logout = async () => { try { await authLogout(); } finally { window.location.href = "/login"; } };

    const s = me?.summary || {};

    return (
        <div className="min-h-screen bg-background" data-testid="zone-admin-dashboard">
            {/* iter181 — this console never registered the device or polled for alerts (landmine 21): a branch
                admin who stayed here, or whose shop access is "none", received nothing. */}
            <OrderAlertFeed />
            <NotificationGate />
            <div className="bg-gradient-to-br from-indigo-700 to-violet-800 text-white px-4 pt-5 pb-6 rounded-b-3xl">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">Zone Admin</p>
                        <p className="font-bold text-lg leading-tight">{me?.user?.first_name || "Welcome"} {me?.user?.last_name || ""}</p>
                    </div>
                    <div className="flex gap-1">
                        <button onClick={loadAll} data-testid="za-dash-refresh" className="p-2 rounded-lg hover:bg-white/10"><RefreshCcw size={16} /></button>
                        <button onClick={logout} data-testid="za-dash-logout" className="p-2 rounded-lg hover:bg-white/10"><LogOut size={16} /></button>
                    </div>
                </div>

                <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-4xl font-black" data-testid="za-balance">{inr(s.balance || 0)}</span>
                    <span className="text-xs opacity-75">pending payout</span>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 text-[11px]">
                    <KpiTile icon={Coins} label="Commission" value={s.earn_commission} testid="za-kpi-commission" />
                    <KpiTile icon={Truck} label="Delivery" value={s.earn_delivery} testid="za-kpi-delivery" />
                    <KpiTile icon={Wallet} label="Paid" value={s.total_paid} testid="za-kpi-paid" />
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 pt-4">
                {me?.shop_access && me.shop_access !== "NONE" && (
                    <div className="mb-3 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 border border-indigo-200 dark:border-indigo-900 rounded-2xl p-4 flex items-center gap-3" data-testid="za-shops-cta">
                        <Store size={28} className="text-indigo-600" />
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">Manage shops in your zone</p>
                            <p className="text-xs text-muted-foreground">
                                {me.shop_access === "EDIT" ? "Full edit access — menu, pricing, hours, orders." : "View-only access — browse shops, menu, and orders."}
                            </p>
                        </div>
                        <a href="/merchant" className="bg-indigo-600 text-white rounded-xl px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5" data-testid="za-open-merchant">Open</a>
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Card icon={MapPin} label="Zones managed" value={me?.zones?.length || 0} subtitle={(me?.zones || []).map((z) => z.name).join(" · ") || "None yet"} />
                    <Card icon={Users} label="Zone drivers" value={me?.zone_drivers || 0} subtitle="Settled offline" />
                </div>

                <div className="mt-5 flex gap-1.5 text-xs font-bold">
                    {[["ledger", "Ledger"], ["drivers", "Drivers"], ["orders", "Orders"]].map(([k, l]) => (
                        <button key={k} data-testid={`za-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 rounded-xl py-2 ${tab === k ? "bg-foreground text-background" : "bg-card border border-border"}`}>{l}</button>
                    ))}
                </div>

                {tab === "ledger" && (
                    <div className="mt-3">
                        <div className="flex flex-wrap gap-1 text-[11px] font-bold mb-2">
                            {FILTERS.map(([k, t, l]) => (
                                <button key={t} data-testid={`za-filter-${t}`} onClick={() => setFilter(k)} className={`px-2.5 py-1 rounded-full border ${filter === k ? "bg-foreground text-background border-foreground" : "bg-card border-border text-muted-foreground"}`}>{l}</button>
                            ))}
                        </div>
                        {loading && <p className="text-center text-sm text-muted-foreground py-6"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</p>}
                        {!loading && ledger.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No ledger entries yet.</p>}
                        <div className="space-y-2">
                            {ledger.map((e) => {
                                const negative = e.kind === "PAYOUT" || (e.kind === "ADJUSTMENT" && e.amount < 0);
                                const tone = {
                                    EARNING_COMMISSION: "bg-emerald-100 text-emerald-700",
                                    EARNING_DELIVERY: "bg-sky-100 text-sky-700",
                                    PAYOUT: "bg-rose-100 text-rose-700",
                                    ADJUSTMENT: "bg-amber-100 text-amber-700",
                                }[e.kind] || "bg-muted text-muted-foreground";
                                return (
                                    <div key={e.id} data-testid={`za-led-${e.id}`} className="bg-card border border-border rounded-2xl px-3 py-2 flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${tone}`}>{e.kind.replace("EARNING_", "")}</span>
                                                {e.order_unique_id && <span className="text-[11px] font-bold text-muted-foreground">#{e.order_unique_id}</span>}
                                                {e.zone_name && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{e.zone_name}</span>}
                                                {e.breakdown && <BreakdownPopover entry={e} />}
                                            </div>
                                            {e.note && <p className="text-xs mt-0.5 truncate">{e.note}</p>}
                                            <p className="text-[11px] text-muted-foreground">{fmtDate(e.created_at)}</p>
                                        </div>
                                        <p className={`font-black text-sm ${negative ? "text-rose-600" : "text-emerald-700"}`}>{negative ? "−" : "+"}{inr(Math.abs(e.amount))}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tab === "drivers" && (
                    <div className="mt-3 space-y-2">
                        {drivers.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No drivers classified under you yet — ask the tenant admin to add some.</p>}
                        {drivers.map((d) => (
                            <div key={d.id} data-testid={`za-driver-${d.id}`} className="bg-card border border-border rounded-2xl px-3 py-2 flex items-center gap-3">
                                <div className="flex-1">
                                    <p className="font-semibold text-sm">{d.name || "—"}</p>
                                    <p className="text-xs text-muted-foreground">+91 {d.mobile_number} · {d.vehicle_type || "BIKE"} · {d.status || "PENDING"}</p>
                                </div>
                                <p className="text-xs font-bold">{d.total_deliveries || 0} drops</p>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "orders" && (
                    <div className="mt-3 space-y-2">
                        {orders.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No attributed orders yet.</p>}
                        {orders.map((o) => (
                            <div key={o.id} data-testid={`za-order-${o.id}`} className="bg-card border border-border rounded-2xl px-3 py-2">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-semibold text-sm">#{o.order_unique_id} <span className="text-xs text-muted-foreground font-normal">· {o.restaurant_name}</span></p>
                                        <p className="text-[11px] text-muted-foreground">{fmtDate(o.created_at)} · {o.zone_name || "—"} · {o.status}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-black text-sm text-emerald-700">{inr(o?.totals?.grand_total || 0)}</p>
                                        <p className="text-[10px] text-muted-foreground">Delivery {inr(o?.totals?.delivery_fee || 0)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function KpiTile({ icon: Icon, label, value, testid }) {
    return (
        <div data-testid={testid} className="bg-white/15 rounded-xl px-2 py-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider opacity-80"><Icon size={10} /> {label}</div>
            <p className="font-black text-base mt-0.5">{inr(value || 0)}</p>
        </div>
    );
}


function Card({ icon: Icon, label, value, subtitle }) {
    return (
        <div className="bg-card border border-border rounded-2xl px-3 py-3">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground"><Icon size={11} /> {label}</div>
            <p className="font-black text-xl mt-0.5">{value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{subtitle}</p>
        </div>
    );
}


function BreakdownPopover({ entry }) {
    const b = entry.breakdown || {};
    const isCommission = entry.kind === "EARNING_COMMISSION";
    const isDelivery = entry.kind === "EARNING_DELIVERY";
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`za-led-breakdown-${entry.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 hover:text-indigo-900 underline decoration-dotted underline-offset-2"
                    title="How was this computed?"
                >
                    <Info size={11} /> Why?
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="top"
                className="w-72 p-3 text-xs space-y-2"
                data-testid={`za-led-breakdown-popover-${entry.id}`}
            >
                <div className="flex items-center justify-between gap-2 pb-1 border-b border-border">
                    <span className="font-bold uppercase tracking-wider text-[10px]">Per-order breakdown</span>
                    {entry.order_unique_id && <span className="text-[10px] text-muted-foreground">#{entry.order_unique_id}</span>}
                </div>

                {isCommission && (
                    <>
                        <Row label="Commission type" value={b.commission_type || "—"} />
                        <Row label={b.base_label || "Base"} value={inr(b.base || 0)} />
                        <Row label="Rate" value={`${b.pct ?? 0}%`} />
                        <div className="flex items-center justify-between pt-2 mt-1 border-t border-border">
                            <span className="font-bold">Earned</span>
                            <span className="font-black text-emerald-700">
                                {`${b.pct ?? 0}% of ${inr(b.base || 0)} = ${inr(b.computed ?? entry.amount)}`}
                            </span>
                        </div>
                    </>
                )}

                {isDelivery && (
                    <>
                        <p className="text-muted-foreground">
                            Customer paid a delivery fee on this order. Because your zone driver fulfilled it, the fee was redirected to you to settle the rider offline.
                        </p>
                        {b.rider_name && <Row label="Zone driver" value={b.rider_name} />}
                        <Row label="Delivery fee" value={inr(b.delivery_fee ?? entry.amount)} />
                        <div className="flex items-center justify-between pt-2 mt-1 border-t border-border">
                            <span className="font-bold">Earned</span>
                            <span className="font-black text-emerald-700">{inr(b.delivery_fee ?? entry.amount)}</span>
                        </div>
                    </>
                )}

                {!isCommission && !isDelivery && (
                    <p className="text-muted-foreground">No breakdown available for this entry type.</p>
                )}
            </PopoverContent>
        </Popover>
    );
}


function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-semibold">{value}</span>
        </div>
    );
}
